import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { LoggingService } from '@core/services/logging.service';
import {
  isTenableCredentialsNotFoundBody,
  isTenableUnauthorizedBody,
  parseTenableFunctionBody,
} from '@core/tenable/models/tenable-account-unauthorized.error';

/**
 * BackendResilienceInterceptor
 *
 * Keeps the dashboard usable when a backend data source is unavailable.
 *
 * Tenable.io is retained for some customers, but its Azure Functions
 * (`/api/tenable/*`) — and the in-house Ninja-Agent-X API (`/api/v1/*`) — can
 * return transient or tenant-specific failures in production: the function app
 * is down (502/503/504), the company/credentials/scan is not found (404), or
 * auth is rejected (401/403). Previously these surfaced as unhandled
 * `HttpErrorResponse`s that reached Angular's global `ErrorHandler` and broke
 * whole pages on load.
 *
 * For **read** calls to those backends, this interceptor converts such failures
 * into a graceful, empty-but-valid `200` response shaped to match what the
 * calling service expects (e.g. `{ data: { assets: [] } }`, `{ scans: [] }`).
 * The caller renders an empty state instead of crashing.
 *
 * **Mutations are never swallowed** — create/launch/delete/update/etc. errors
 * propagate unchanged so user-initiated actions still report failures.
 */
@Injectable()
export class BackendResilienceInterceptor implements HttpInterceptor {
  private readonly logging = inject(LoggingService);

  /** HTTP error statuses that should degrade to an empty result for reads. */
  private readonly softFailStatuses = new Set([
    0, 401, 403, 404, 408, 429, 500, 502, 503, 504,
  ]);

  /**
   * Read routes whose failures must NEVER be degraded to an empty `200`.
   *
   * The interceptor's premise is that an empty-but-valid body is a safer
   * outcome than a crash. That holds for a list of things. It does not hold
   * where the empty value is itself a claim:
   *
   * - `trust-score` is a verdict on whether an artifact is safe to install.
   *   There is no empty-but-valid trust score — and because these URLs contain
   *   `/api/v1/scans/`, the generic branch handed back the *scan-detail*
   *   placeholder (`status: 'failed'`, `scan_type: 'unknown'`), which Angular
   *   accepts as a `NinjaAgentTrustScore` and the panel renders as a verdict.
   * - `sarif` is fetched as a Blob for download. A degraded body becomes a
   *   Blob too and is saved as `scan-*.sarif.json`, handing the user a corrupt
   *   file instead of an error.
   * - `agent-artifacts` is a registry that asserts completeness. Degrading it
   *   renders an outage, a 401 or a 403 as "No agent artifacts scanned yet" —
   *   the same failed-read-as-clean class as #855.
   *
   * Every caller of these three already rethrows and every component has an
   * error state, so passing the failure through is what they were written for.
   */
  private readonly neverDegradeFragments = [
    '/trust-score',
    '/sarif',
    '/agent-artifacts',
  ];

  /**
   * URL fragments that identify a state-changing (mutation) Tenable/Ninja call.
   * Failures on these are passed through so the user sees the action failed.
   */
  private readonly mutationFragments = [
    'create-',
    'delete-',
    'launch-',
    'stop-scan',
    'force-stop',
    'pause-scan',
    'resume-scan',
    'update-scan',
    'scanner/update',
    'scanner/restart',
    'scanner/unlink',
    'rename-folder',
    'cycle-company-tokens',
    'generate-auth-keys',
    'validate-user-creds',
    'create-user-credentials',
  ];

  /**
   * Intercepts outgoing HTTP requests and, for resilient-target **read** calls,
   * converts soft backend failures into an empty `200` so the app keeps working.
   * Non-target requests, mutations, and non-soft errors pass through unchanged.
   *
   * @param req The outgoing HTTP request.
   * @param next The next handler in the interceptor chain.
   * @returns An observable of HTTP events — either the real response, or a
   *   synthesized empty `200` when a resilient-target read fails softly.
   */
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isResilientTarget(req) || this.mustNotDegrade(req)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (
          error instanceof HttpErrorResponse &&
          this.softFailStatuses.has(error.status) &&
          !this.isMutation(req) &&
          !this.isTenableCredentialFailure(error)
        ) {
          this.logging.logInfo(
            'BackendResilienceInterceptor: degraded a failed read to an empty result',
            {
              url: req.url,
              method: req.method,
              status: error.status,
            },
          );
          return of(
            new HttpResponse({
              status: 200,
              statusText: 'OK (degraded)',
              url: req.url,
              body: this.emptyBodyFor(req.url),
            }),
          );
        }
        // Mutations and non-soft errors propagate unchanged.
        throw error;
      }),
    );
  }

  /**
   * Determines whether a request targets a backend this interceptor protects
   * (the Tenable functions or the Ninja-Agent-X API).
   *
   * @param req The outgoing HTTP request.
   * @returns `true` for `/api/tenable/*` and Ninja-Agent-X `/api/v1/*` requests.
   */
  private isResilientTarget(req: HttpRequest<unknown>): boolean {
    const url = req.url || '';
    if (url.includes('/api/tenable/')) {
      return true;
    }
    const ninjaBase = environment.ninja_agent_api_url;
    if (ninjaBase && url.startsWith(ninjaBase) && url.includes('/api/v1/')) {
      return true;
    }
    return false;
  }

  /**
   * Determines whether a read must surface its failure rather than degrade.
   *
   * See `neverDegradeFragments`: these are reads where an empty-but-valid body
   * would itself be a false statement.
   *
   * @param req The outgoing HTTP request.
   * @returns `true` if failures on this route must propagate unchanged.
   */
  /**
   * True when a failed Tenable read says the company's keys are missing or
   * were refused. `TenableApiService` recovers from both by rotating the keys
   * and retrying (#883), and shows the account state when that fails. An empty
   * `200` hides the failure from it, so the keys are never rotated and the page
   * reads "no scans" until someone cycles them by hand.
   *
   * @param error The failed response.
   */
  private isTenableCredentialFailure(error: HttpErrorResponse): boolean {
    const body = parseTenableFunctionBody(error.error);
    return (
      isTenableUnauthorizedBody(body) || isTenableCredentialsNotFoundBody(body)
    );
  }

  private mustNotDegrade(req: HttpRequest<unknown>): boolean {
    const url = req.url || '';
    return this.neverDegradeFragments.some((frag) => url.includes(frag));
  }

  /**
   * Determines whether a request is a state-changing operation whose failures
   * must surface to the caller (and so must NOT be degraded to an empty result).
   * Ninja-Agent-X is classified by HTTP verb (non-GET = mutation); the all-POST
   * Tenable functions are classified by URL fragment.
   *
   * @param req The outgoing HTTP request.
   * @returns `true` if the request mutates state and should pass through on error.
   */
  private isMutation(req: HttpRequest<unknown>): boolean {
    const url = req.url || '';
    const method = (req.method || 'GET').toUpperCase();

    // Ninja-Agent-X is RESTful: anything but GET is a mutation.
    const ninjaBase = environment.ninja_agent_api_url;
    if (ninjaBase && url.startsWith(ninjaBase)) {
      return method !== 'GET';
    }

    // Tenable functions are all POST; classify by URL fragment.
    if (method === 'PUT' || method === 'DELETE') {
      return true;
    }
    return this.mutationFragments.some((frag) => url.includes(frag));
  }

  /**
   * Produces an empty-but-valid response body matching the shape the calling
   * service expects for the given endpoint, so downstream mapping never throws.
   *
   * @param url The request URL, used to select the endpoint-appropriate shape.
   * @returns An empty payload (e.g. `{ data: { assets: [] } }`, `{ scans: [] }`,
   *   or a scan-detail `{ info, hosts, vulnerabilities }`) for the endpoint.
   */
  private emptyBodyFor(url: string): unknown {
    const u = url.toLowerCase();

    // ---- Ninja-Agent-X (`/api/v1/*`) -------------------------------------
    // Per-asset findings. Must precede the `/api/v1/assets` list branch, which
    // would otherwise match and return `{ assets: [] }`: the caller read that
    // as "zero findings", so a failed lookup rendered as a clean asset with a
    // perfect score. `limit: 0` marks the read as degraded — a real response
    // always echoes the positive limit it was served with.
    if (/\/api\/v1\/assets\/[^/?#]+\/findings/.test(u)) {
      return {
        asset_id: this.matchPathSegment(url, '/api/v1/assets/') ?? '',
        findings: [],
        total: 0,
        limit: 0,
        offset: 0,
      };
    }
    if (u.includes('/api/v1/assets')) {
      return { assets: [], total: 0, limit: 0, offset: 0 };
    }
    if (u.includes('/api/v1/findings')) {
      return { findings: [], total: 0 };
    }
    if (u.includes('/api/v1/scans')) {
      // Covers list, single scan, and findings-by-scan reads.
      if (u.includes('/findings')) {
        return { findings: [], total: 0 };
      }
      const scanId = this.matchPathSegment(url, '/api/v1/scans/');
      if (scanId) {
        return {
          id: scanId,
          name: 'Unavailable scan',
          scan_type: 'unknown',
          status: 'failed',
          findings_count: 0,
          created_at: new Date(0).toISOString(),
        };
      }
      return { scans: [], total: 0 };
    }
    if (u.includes('/api/v1/compliance')) {
      // Availability dashboard endpoints. These callers expect specific shapes:
      // `trend`/`history` map over arrays, `metrics` reads scalar fields. A
      // generic `{ ... }` object here makes `trend.map` throw and crashes the
      // page, so return shape-accurate empties.
      if (u.includes('/availability/')) {
        // Array-returning reads (`getMonthlyTrend`, `getMetricsHistory`).
        if (u.includes('/availability/trend') || u.includes('/availability/history')) {
          return [];
        }
        // Scalar metrics object (`getMetrics`); zeroed but fully typed so the
        // dashboard renders an empty state instead of NaN/undefined.
        if (u.includes('/availability/metrics')) {
          return {
            currentCompanyId: '',
            timestamp: new Date(0).toISOString(),
            uptime: 0,
            downtime: 0,
            avgLatency: 0,
            p95Latency: 0,
            p99Latency: 0,
            errorRate: 0,
            regions: [],
            incidents: [],
          };
        }
        // SLA status / config and other availability object reads.
        return {};
      }
      if (u.includes('/api/v1/compliance/cmmc/control-findings-live')) {
        return {
          findings: [],
          total: 0,
          generatedAt: new Date(0).toISOString(),
        };
      }
      if (u.includes('/api/v1/compliance/cmmc/summary-live')) {
        return {
          counts: {
            met: 0,
            partial: 0,
            not_met: 0,
            not_applicable: 0,
          },
          byDomain: {},
          total: 0,
          score: 0,
        };
      }
      // FedRAMP / TX-RAMP live reads. Same hazard as the availability endpoints
      // above: the generic object below has no `counts`, and the overview
      // template reads `auto.counts['met']` directly. A missing `counts` throws
      // inside change detection, which aborts the whole view — the FedRAMP
      // crosswalk silently disappeared and stopped updating on every impact-level
      // switch because of it. `coverage` is required for the same reason.
      if (
        u.includes('/api/v1/compliance/fedramp/control-findings-live') ||
        u.includes('/api/v1/compliance/txramp/control-findings-live')
      ) {
        return {
          findings: [],
          total: 0,
          generatedAt: new Date(0).toISOString(),
        };
      }
      if (
        u.includes('/api/v1/compliance/fedramp/summary-live') ||
        u.includes('/api/v1/compliance/txramp/summary-live')
      ) {
        return {
          counts: {
            met: 0,
            partial: 0,
            not_met: 0,
            not_applicable: 0,
            not_assessed: 0,
            attestation_required: 0,
          },
          byFamily: {},
          total: 0,
          score: 0,
          baselineScore: 0,
          coverage: {
            baselineTotal: 0,
            assessed: 0,
            notAssessed: 0,
            attestationRequired: 0,
            coveragePct: 0,
          },
        };
      }
      // NIST CSF 2.0 live reads. `frameworkTotal: 0` (a real response always
      // says 106) and empty catalog arrays are how the CSF module tells a
      // degraded read apart from a tenant that genuinely has no scans, so it
      // renders "could not load" instead of a zero score.
      if (u.includes('/api/v1/compliance/csf2/catalog')) {
        return {
          framework: 'NIST CSF 2.0',
          functions: [],
          categories: [],
          subcategories: [],
        };
      }
      if (u.includes('/api/v1/compliance/csf2/control-findings-live')) {
        return {
          findings: [],
          total: 0,
          generatedAt: new Date(0).toISOString(),
        };
      }
      // `frameworkTotal: 0` marks the crosswalk as degraded; the module then
      // shows "could not load" rather than a table of zeros.
      if (u.includes('/api/v1/compliance/csf2/crosswalk-live')) {
        return {
          frameworkTotal: 0,
          combinedEvidenced: 0,
          sources: [],
          generatedAt: new Date(0).toISOString(),
        };
      }
      if (u.includes('/api/v1/compliance/csf2/summary-live')) {
        return {
          counts: {},
          byFunction: {},
          byCategory: {},
          total: 0,
          assessed: 0,
          score: 0,
          frameworkScore: 0,
          coverage: {
            frameworkTotal: 0,
            automated: 0,
            attestationRequired: 0,
            notAssessed: 0,
          },
        };
      }
      return { frameworks: [], controls: [], findings: [], total: 0 };
    }

    // ---- Tenable functions (`/api/tenable/*`) ----------------------------
    if (u.includes('list-assets') || u.includes('asset-details')) {
      return { success: true, data: { total_count: 0, assets: [] } };
    }
    if (u.includes('scanner')) {
      return { success: true, data: [], scanners: [] };
    }
    if (u.includes('child-account')) {
      return { success: true, data: [], accounts: [] };
    }
    if (
      u.includes('scan-status') ||
      u.includes('latest-scan-status') ||
      u.includes('latest-scan-progress')
    ) {
      return { success: true, data: { status: 'unknown', progress: 0 } };
    }
    if (u.includes('template')) {
      return { success: true, data: [], templates: [] };
    }
    if (u.includes('list-folders')) {
      return { success: true, data: [], folders: [] };
    }
    if (u.includes('host-details')) {
      return { success: true, data: { info: {}, vulnerabilities: [] } };
    }
    // Single scan detail: `.../api/tenable/scans/{id}` or `scan/editor/{id}`.
    if (/\/scans\/[^/]+/.test(u) || u.includes('scan/editor')) {
      return {
        success: true,
        data: {
          info: {},
          hosts: [],
          vulnerabilities: [],
          compliance: [],
          history: [],
          notes: [],
          remediations: [],
        },
      };
    }
    if (u.includes('list-scan')) {
      return { success: true, data: [], scans: [] };
    }

    // Generic safe default.
    return { success: true, data: [] };
  }

  /**
   * Extracts the next path segment after a known prefix, ignoring query/hash
   * suffixes. Returns `null` when the prefix is absent or points to a list path.
   *
   * @param url Request URL.
   * @param prefix Lower-case path prefix ending in `/`.
   * @returns The decoded path segment, or `null`.
   */
  private matchPathSegment(url: string, prefix: string): string | null {
    const index = url.toLowerCase().indexOf(prefix);
    if (index === -1) {
      return null;
    }
    const tail = url.slice(index + prefix.length);
    const segment = tail.split(/[/?#]/)[0];
    return segment || null;
  }
}
